import React from 'react'
import styled from 'styled-components'
import { Checkbox } from 'semantic-ui-react'
import { useDidUpdate } from 'react-nb-hooks'
import { translate } from 'loankeeper-common/helpers'
import { LKIcon, LKButton, LKTextArea } from 'loankeeper-common/components'

import LKModal from './LKModal'
import LKModalContainer from './container'
import { open } from './actions'

export const openConfirmation = (name, { message, ...rest } = {}) =>
  open({ name, message: message || translate('LKMESSAGE-MSG_ARE_YOU_SURE'), ...rest }, rest.code)

function usePriorityModals() {
  React.useLayoutEffect(() => {
    document.body.classList.add('priority-modals')

    return () => {
      document.body.classList.remove('priority-modals')
    }
  }, [])
}

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  .lk-icon {
    margin-right: 10px;
  }

  .agreement {
    margin-top: 20px;
  }
`

const LKButtonOkay = styled(LKButton)`
  width: 120px;
`

const DescriptionWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  .lk-textarea {
    width: 530px;
    padding: 7px;
    resize: vertical;
  }
`

const ConfirmationModal = (props) => {
  const {
    modalInstanceName,
    isOpen,
    message,
    agreeable,
    agreeText,
    loading,
    warning,
    withDescription,
    params,
    onConfirm,
    onClose,
    closeModal,
  } = props

  usePriorityModals()

  const [state, setState] = React.useState({
    agreed: false,
    description: '',
  })

  useDidUpdate(() => {
    if (!isOpen) {
      setState({ agreed: false })
    }
  }, [isOpen])

  const handleCheckboxChange = React.useCallback(
    (_, { checked }) => {
      setState({ agreed: checked })
    },
    [setState],
  )

  React.useEffect(() => {
    if (params?.descriptionText) {
      setState((prevState) => ({
        ...prevState,
        description: params.descriptionText,
      }))
    }
  }, [params?.descriptionText])

  const handleConfirm = React.useCallback(() => {
    if (!agreeable || state.agreed) {
      closeModal()
      onConfirm(params, state.description)
    }
  }, [agreeable, state, params, onConfirm, closeModal])

  const handleChangeDescription = React.useCallback(
    (e, { value }) => {
      setState({ description: value })
    },
    [setState],
  )

  function handleClose() {
    if (typeof onClose === 'function') {
      onClose({ params })
    }
  }

  return (
    <LKModal
      name={modalInstanceName}
      heading={warning ? translate('LKMESSAGE-MSG_WARNING') : translate('LKMESSAGE-MSG_CONFIRM')}
      dataAttributes={params.code && { 'data-code': params.code }}
      onClose={handleClose}
      size="xxs"
      footer={
        <LKModal.Footer>
          <LKButtonOkay alias="ok" onClick={handleConfirm} loading={loading} disabled={loading}>
            {translate('frm_dialog_check-cmd_finish')}
          </LKButtonOkay>

          <LKModal.CancelButton text={translate('frm_dialog_check-cmd_cancel')} />
        </LKModal.Footer>
      }
    >
      <Wrapper>
        {!withDescription && <LKIcon alias={warning ? 'alert' : 'question'} size={25} />}

        <div>
          {params.customHtml && <span dangerouslySetInnerHTML={{ __html: params.customHtml }} />}
          <p>{message}</p>

          {withDescription && (
            <DescriptionWrapper>
              <LKTextArea value={state.description} onChange={handleChangeDescription} height={150} maxLength="2000" />
            </DescriptionWrapper>
          )}

          {agreeable && (
            <Checkbox
              name="ConfirmCheck"
              className="agreement"
              checked={state.agreed}
              onChange={handleCheckboxChange}
              label={agreeText}
            />
          )}
        </div>
      </Wrapper>
    </LKModal>
  )
}

export default LKModalContainer()(ConfirmationModal)
